import {
	AlertTriangle,
	CheckCircle,
	InfoCircle,
	XClose,
} from "@untitledui/icons";
import type { FC, MouseEvent } from "react";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { cx } from "@/utils/cx";

export type ToastVariant = "success" | "error" | "warning";

export type ToastProps = {
	variant: ToastVariant;
	title: string;
	description?: string;
	onClose?: () => void;
	className?: string;
};

const variantConfig = {
	success: {
		icon: CheckCircle,
		color: "success" as const,
	},
	error: {
		icon: InfoCircle,
		color: "error" as const,
	},
	warning: {
		icon: AlertTriangle,
		color: "warning" as const,
	},
};

export const Toast: FC<ToastProps> = ({
	variant,
	title,
	description,
	onClose,
	className,
}) => {
	const config = variantConfig[variant];

	const handleClose = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (onClose) {
			onClose();
		}
	};

	return (
		<output
			aria-atomic="true"
			aria-live="polite"
			className={cx(
				"pointer-events-auto flex w-full min-w-[320px] max-w-[400px] gap-3 rounded-xl border border-primary bg-primary p-4 shadow-lg",
				className
			)}
			data-toast
			data-variant={variant}
		>
			<FeaturedIcon
				aria-hidden="true"
				color={config.color}
				data-testid={`toast-icon-${variant}`}
				icon={config.icon}
				size="lg"
				theme="outline"
			/>

			<div className="flex flex-1 flex-col gap-1">
				<h3 className="font-semibold text-fg-primary text-sm">{title}</h3>
				{description && (
					<p className="text-fg-secondary text-sm">{description}</p>
				)}
			</div>

			{onClose && (
				<button
					aria-label="Fechar notificação"
					className="flex shrink-0 cursor-pointer items-start justify-center text-fg-quaternary transition-colors hover:text-fg-secondary focus:outline-none focus-visible:text-fg-secondary focus-visible:ring-2 focus-visible:ring-focus-ring"
					onClick={handleClose}
					type="button"
				>
					<XClose aria-hidden="true" className="z-[90] h-5 w-5" />
				</button>
			)}
		</output>
	);
};
